import React from "react";
import { ProductConsumer } from "../../Context/AddProWakel";
import {
    modelLabel,
    actionLabel,
    formatDate,
    changedFieldDetails,
    changedSubTableLabels,
    changedSubTableItems,
    recordIdentity,
    EMPTY,
} from "./auditFields";

const ITEM_KIND = {
    added: { icon: "fa-plus", text: "زیادکرا" },
    removed: { icon: "fa-minus", text: "لابرا" },
    changed: { icon: "fa-pen", text: "" },
};

// One edited line item of an invoice: which item, and only the columns that
// moved on it. Added / removed lines have no diff to show, so they carry the
// word instead.
const ChangedItem = ({ entry }) => {
    const kind = ITEM_KIND[entry.kind] || ITEM_KIND.changed;
    const label = [entry.item.code, entry.item.name].filter(Boolean).join(" — ");
    const summary = entry.fields
        .map((f) => `${f.label}: ${f.before} ← ${f.after}`)
        .join(" | ");

    return (
        <span
            className={`rs-change rs-change--item rs-change--${entry.kind}`}
            title={`${entry.table} · ${label || EMPTY}${summary ? ` · ${summary}` : ""}`}
        >
            <i className={`fas ${kind.icon} rs-change__kind`}></i>
            <span className="rs-change__label">{label || entry.table}</span>
            {kind.text ? (
                <span className="rs-change__new">{kind.text}</span>
            ) : (
                entry.fields.map((f) => (
                    <span key={f.key} className="rs-change__field">
                        <span className="rs-change__sublabel">{f.label}</span>
                        <span className="rs-change__old">{f.before}</span>
                        <i className="fas fa-arrow-left rs-change__arrow"></i>
                        <span className="rs-change__new">{f.after}</span>
                    </span>
                ))
            )}
        </span>
    );
};

// A row answers who / what / when / what changed. The raw `documentId` hash and
// the IP address are not actionable at a glance, so they live in the modal.
//
// Every field an edit touched is listed here — nothing is collapsed into a
// "+N more" chip. Each one spells out old → new (کۆن → نوێ) as coloured pills,
// so a whole edit reads without opening the row. Line-item tables report the
// individual items that moved, not just "the item table changed". Rows for
// records with many columns (a sale, a product) get tall; that is intended. The
// list is capped at one page of rows in the context, which keeps the cost flat.
const ChangedFields = ({ log }) => {
    if (log.action === "DELETE") {
        return <span className="rs-dim">تۆمارەکە سڕایەوە</span>;
    }

    const details = changedFieldDetails(log);
    const items = changedSubTableItems(log);
    // The sub-table label is the fallback for a line-item change the row diff
    // cannot name — a pure reorder, or an edit confined to plumbing keys.
    const subLabels = items.length > 0 ? [] : changedSubTableLabels(log);
    if (details.length === 0 && items.length === 0 && subLabels.length === 0) {
        return <span className="rs-dim">{EMPTY}</span>;
    }

    return (
        <div className="rs-changes">
            {details.map((d) => (
                <span
                    key={d.key}
                    className="rs-change"
                    title={`${d.label}: ${d.before} ← ${d.after}`}
                >
                    <span className="rs-change__label">{d.label}</span>
                    <span className="rs-change__old">{d.before}</span>
                    <i className="fas fa-arrow-left rs-change__arrow"></i>
                    <span className="rs-change__new">{d.after}</span>
                </span>
            ))}
            {items.map((entry) => (
                <ChangedItem key={entry.id} entry={entry} />
            ))}
            {subLabels.map((label) => (
                <span key={label} className="rs-change rs-change--table">
                    <i className="fas fa-table"></i>
                    {label}
                </span>
            ))}
        </div>
    );
};

// Model cell: the section name, plus who or what the record is — a product's
// code + name, or on an invoice the customer's number + name — so the row can
// be recognised at a glance.
const SectionCell = ({ log }) => {
    const id = recordIdentity(log);
    return (
        <>
            <span className="rs-strong">{modelLabel(log.modelName)}</span>
            {id && (id.code || id.name) ? (
                <span className="rs-sub">
                    <i
                        className={`fas ${id.kind === "customer" ? "fa-user" : "fa-box"} rs-sub__icon`}
                    ></i>
                    {id.code ? <span className="rs-code">{id.code}</span> : null}
                    {id.code && id.name ? " — " : null}
                    {id.name}
                </span>
            ) : null}
        </>
    );
};

// One rendered row. Memoised on the log object: paging and badge polls re-render
// the provider, and re-rendering an unchanged row would rebuild every chip in it.
const LogRow = React.memo(({ log, index, onOpen }) => {
    const isDelete = log.action === "DELETE";
    return (
        <tr
            className={`rs-clickable ${isDelete ? "rs-row--danger" : "rs-row--accent"}`}
            onClick={() => onOpen(log._id)}
        >
            <td className="is-center rs-idx">{index}</td>
            <td className="rs-num">{formatDate(log.createdAt)}</td>
            <td className="is-center">
                <span
                    className={`rs-badge ${isDelete ? "rs-badge--red" : "rs-badge--amber"}`}
                >
                    <i className={`fas ${isDelete ? "fa-trash" : "fa-pen"}`}></i>
                    {actionLabel(log.action)}
                </span>
            </td>
            <td>
                <SectionCell log={log} />
            </td>
            <td>
                <ChangedFields log={log} />
            </td>
            <td>
                {log.performedBy ? (
                    <span className="rs-strong">{log.performedBy.name}</span>
                ) : (
                    <span className="rs-dim">نەزانراو</span>
                )}
            </td>
        </tr>
    );
});

class ScroollTableAuditLog extends React.Component {
    render() {
        return (
            <ProductConsumer>
                {(value) => {
                    // AL_view is already filtered and sliced to the current page by
                    // the context — the table does no work per keystroke.
                    const {
                        AL_view,
                        AL_OpenModal,
                        AL_searchTerm,
                        AL_page,
                        AL_pageSize,
                    } = value;
                    const rows = AL_view || [];
                    const searching = (AL_searchTerm || "").trim() !== "";
                    const offset = ((AL_page || 1) - 1) * (AL_pageSize || 30);

                    return (
                        <div className="rs-card">
                            <div className="rs-table-wrap">
                                <table className="rs-table">
                                    <thead>
                                        <tr>
                                            <th className="is-center" style={{ width: "48px" }}>
                                                #
                                            </th>
                                            <th style={{ width: "150px" }}>بەروار و کات</th>
                                            <th className="is-center" style={{ width: "110px" }}>
                                                کردار
                                            </th>
                                            <th style={{ width: "220px" }}>بەش / کاڵا و کڕیار</th>
                                            <th>گۆڕانکاری</th>
                                            <th style={{ width: "160px" }}>لەلایەن</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((log, i) => (
                                            <LogRow
                                                key={log._id || i}
                                                log={log}
                                                index={offset + i + 1}
                                                onOpen={AL_OpenModal}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {rows.length === 0 && (
                                <div className="rs-empty">
                                    <i className="fas fa-inbox"></i>
                                    <p>
                                        {searching
                                            ? "هیچ تۆمارێک لەگەڵ گەڕانەکەت نەگونجا"
                                            : "تۆمارێک نەدۆزرایەوە"}
                                    </p>
                                </div>
                            )}
                        </div>
                    );
                }}
            </ProductConsumer>
        );
    }
}

export default ScroollTableAuditLog;
